import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

const AuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const { checkSession } = useAuth();
  const { showSuccess } = useToast();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Guard: Only process auth callback if we're actually on the callback route
    if (!window.location.pathname.includes('/auth/callback')) {
      return;
    }
    
    const handleAuthCallback = async () => {
      try {
        
        // IMPORTANT: Capture hash immediately before React Router can clear it
        const currentHash = window.location.hash;
        const currentSearch = window.location.search;
        const fullUrl = window.location.href;
        
        // Try extracting from the full URL if hash is empty
        let fragmentString = currentHash.substring(1);
        if (!fragmentString && fullUrl.includes('#')) {
          fragmentString = fullUrl.split('#')[1];
        }
        
        // Check for explicit errors first
        const searchParams = new URLSearchParams(currentSearch);
        const fragmentParams = new URLSearchParams(fragmentString);
        
        const urlError = searchParams.get('error') || fragmentParams.get('error');
        const errorDescription = searchParams.get('error_description') || fragmentParams.get('error_description');

        if (urlError) {
          console.error('OAuth error:', urlError, errorDescription);
          setError(errorDescription || urlError);
          setTimeout(() => navigate('/'), 3000);
          return;
        }

        // Get OAuth tokens from URL (both query params and fragments)
        let accessToken = searchParams.get('access_token') || fragmentParams.get('access_token');
        let refreshToken = searchParams.get('refresh_token') || fragmentParams.get('refresh_token');
        let expiresIn = searchParams.get('expires_in') || fragmentParams.get('expires_in');

        const API_BASE = import.meta.env.VITE_SUPABASE_URL || 'http://localhost:8000';
        const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxvY2FsaG9zdCIsInJvbGUiOiJhbm9uIiwiaWF0IjoxNzM2NzA1NjAwLCJleHAiOjE4NTIzNjI3MDB9.a2kcrdv2Ydk4yHxB4HTkXUdFna-nt4r-OgQW-Tq8a_k';

        // If we have tokens from URL, process them
        if (accessToken) {
          
          // Validate the token
          try {
            const userResponse = await fetch(`${API_BASE}/auth/v1/user`, {
              headers: {
                'apikey': ANON_KEY,
                'Authorization': `Bearer ${accessToken}`
              }
            });

            if (userResponse.ok) {
              await userResponse.json();
            } else {
              throw new Error(`Token validation failed: ${userResponse.status}`);
            }
          } catch (validationError) {
            console.error('Token validation failed:', validationError);
            throw new Error('Invalid OAuth tokens received');
          }

          // Store the session
          const rememberMeStored = localStorage.getItem('cycling-buddy-remember-me');
          const rememberMe = rememberMeStored ? JSON.parse(rememberMeStored) : true;
          
          const session = {
            access_token: accessToken,
            refresh_token: refreshToken || '',
            expires_at: Date.now() + (parseInt(expiresIn || '3600') * 1000)
          };
          
          const storage = rememberMe ? localStorage : sessionStorage;
          storage.setItem('cycling-buddy-session', JSON.stringify(session));
          localStorage.removeItem('cycling-buddy-remember-me');

          // Update auth context
          await checkSession();
          
          // Show success message
          showSuccess('Welcome back! You have been logged in successfully');
          
          // Redirect
          const redirectPath = localStorage.getItem('cycling-buddy-auth-redirect') || '/';
          localStorage.removeItem('cycling-buddy-auth-redirect');
          navigate(redirectPath);
          return;
        }

        try {
          const sessionResponse = await fetch(`${API_BASE}/auth/v1/user`, {
            method: 'GET',
            headers: { 'apikey': ANON_KEY },
            credentials: 'include'
          });

          if (sessionResponse.ok) {
            const userData = await sessionResponse.json();
            
            // Create a session marker for session-based auth
            const tempSession = {
              access_token: 'supabase-session-based',
              user_id: userData.id,
              expires_at: Date.now() + (60 * 60 * 1000) // 1 hour
            };
            
            const rememberMeStored = localStorage.getItem('cycling-buddy-remember-me');
            const rememberMe = rememberMeStored ? JSON.parse(rememberMeStored) : true;
            const storage = rememberMe ? localStorage : sessionStorage;
            
            storage.setItem('cycling-buddy-session', JSON.stringify(tempSession));
            localStorage.removeItem('cycling-buddy-remember-me');
            
            await checkSession();
            showSuccess('Welcome back! You have been logged in successfully');
            
            const redirectPath = localStorage.getItem('cycling-buddy-auth-redirect') || '/';
            localStorage.removeItem('cycling-buddy-auth-redirect');
            navigate(redirectPath);
            return;
          } 
        } catch (sessionError) {
          console.warn('❌ Session-based authentication error:', sessionError);
        }

        // If we reach here, no authentication method worked
        throw new Error('No valid authentication found after OAuth callback');

      } catch (err) {
        console.error('Auth callback error:', err);
        setError('Authentication failed. Please try again.');
        setTimeout(() => navigate('/'), 3000);
      }
    };

    // Delay to ensure URL fragment is available
    const timer = setTimeout(handleAuthCallback, 300);
    return () => clearTimeout(timer);
  }, [navigate, checkSession, showSuccess]);

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="50vh"
        gap={2}
      >
        <Alert severity="error" sx={{ maxWidth: '400px' }}>
          <Typography variant="h6" gutterBottom>
            Authentication Error
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Redirecting to homepage...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="50vh"
      gap={2}
    >
      <CircularProgress size={60} />
      <Typography variant="h6" gutterBottom>
        Completing authentication...
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Please wait while we sign you in
      </Typography>
    </Box>
  );
};

export default AuthCallbackPage;
